import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { type CanActivateFn, Router } from '@angular/router';
import { filter, map, take } from 'rxjs';
import { CurrentAccountService } from '../shared/current-account.service';

/**
 * Reusable functional guard factory that protects routes by a required permission string.
 *
 * Depends ONLY on the `CurrentAccountService` PORT and `Router` — no IAM / app coupling
 * (HR-PORTS-001 COMPLY). The permission string is the only app-specific concern; it is
 * passed by the caller.
 *
 * ## Readiness contract (design D4)
 *
 * Permissions arrive async via the authenticated-initializer's me-query. The guard AWAITS
 * `permissionsLoaded()` before deciding: if the signal is already `true` on entry, the guard
 * resolves synchronously (as an Observable that emits one value immediately); if not, it waits
 * for the first `true` emission before deciding.
 *
 * This prevents two failure modes:
 * - Premature denial: guard decides on an empty set that is still loading.
 * - Flash-of-forbidden: navigation is blocked until the real permission set is known.
 *
 * ## Denial behavior (design D3)
 *
 * On denial, the guard redirects to the application landing route (`/`). No 403 page exists in
 * the scaffold today. NOTE: the bare `/` resolves to the UNGUARDED standalone `dashboard` BC
 * (app-level `pathMatch: 'full'` redirect wins over the admin-level `'' -> hub/launchpad`); this
 * is deliberate — redirecting denials to a guarded route (e.g. hub/launchpad, gated by
 * hub.launchpad.access) would create a redirect loop for users without that permission. Do NOT
 * "fix" the `/` redirect to point at a guarded route. (Open question O1: a dedicated 403 page is
 * a follow-up.)
 *
 * ## Usage example
 *
 * ```typescript
 * // hub.routes.ts
 * {
 *   path: 'launchpad',
 *   canActivate: [permissionGuard('hub.launchpad.access')],
 *   loadComponent: () => import('./features/hub-launchpad.component'),
 * }
 * ```
 *
 * @param permission - The permission key the session must hold to navigate the protected route.
 * @returns A `CanActivateFn` that resolves to `true` (allow) or a `UrlTree` for `/` (redirect).
 */
export const permissionGuard =
  (permission: string): CanActivateFn =>
  () => {
    const account = inject(CurrentAccountService);
    const router = inject(Router);

    // If permissions are already loaded, decide immediately (sync path via Observable).
    // If not, wait for the first `true` emission and then decide.
    return toObservable(account.permissionsLoaded).pipe(
      filter((loaded) => loaded),
      take(1),
      map(() => {
        const allowed = account.hasPermission(permission);
        return allowed ? true : router.parseUrl('/');
      }),
    );
  };
